import React, { useEffect, useRef, useState } from 'react';
import { useImageUpload } from '../../utils/useImageUpload';

interface ChatScreenProps {
  conversation: any;
  chatInput: string;
  setChatInput: (text: string) => void;
  onSendChatMessage: (imageUrl?: string | null) => void;
}

export const ChatScreen: React.FC<ChatScreenProps> = ({
  conversation,
  chatInput,
  setChatInput,
  onSendChatMessage,
}) => {
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const [viewImage, setViewImage] = useState<string | null>(null);

  const { uploading, showPicker, pendingUri } = useImageUpload({
    purpose: 'chat',
    onSuccess: (url) => {
      setPendingImageUrl(url);
    },
    onError: (msg) => {
      alert('Upload Failed: ' + msg);
      setPendingImagePreview(null);
    },
  });

  useEffect(() => {
    if (pendingUri) {
      setPendingImagePreview(pendingUri);
    }
  }, [pendingUri]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [conversation?.messages]);

  const handleSend = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() && !pendingImageUrl) return;
    onSendChatMessage(pendingImageUrl);
    setPendingImageUrl(null);
    setPendingImagePreview(null);
  };

  const getAssetUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return path;
  };

  return (
    <div className="pwa-chat-container">
      {/* Message Feed */}
      <div ref={chatScrollRef} className="pwa-chat-feed">
        {conversation?.messages?.map((msg: any) => (
          <div
            key={msg.id}
            className={`pwa-chat-bubble ${msg.sender === 'user' ? 'pwa-chat-user' : 'pwa-chat-manager'}`}
          >
            {msg.imageUrl && (
              <img
                src={getAssetUrl(msg.imageUrl)}
                alt="Attachment"
                style={{ width: '100%', maxWidth: '200px', borderRadius: '10px', marginBottom: '6px', cursor: 'pointer' }}
                onClick={() => setViewImage(getAssetUrl(msg.imageUrl))}
              />
            )}
            {msg.text && <div>{msg.text}</div>}
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '4px', textAlign: 'right' }}>
              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
            </div>
          </div>
        ))}

        {(!conversation?.messages || conversation.messages.length === 0) && (
          <div className="pwa-empty-state">
            Support chat initialised. A support manager will respond shortly.
          </div>
        )}
      </div>

      {/* Image Preview Bar */}
      {(pendingImagePreview || uploading) && (
        <div style={{ padding: '8px var(--gutter, 16px)', background: 'rgba(7,9,20,0.96)', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {pendingImagePreview && (
            <div style={{ position: 'relative' }}>
              <img src={pendingImagePreview} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }} />
              {uploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: '700' }}>⏳</div>
              )}
            </div>
          )}
          {!pendingImagePreview && uploading && <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Uploading...</span>}
        </div>
      )}

      {/* Input Bar */}
      <form onSubmit={handleSend} className="pwa-chat-input-bar">
        <button
          type="button"
          onClick={showPicker}
          disabled={uploading}
          className="pwa-icon-btn ghost"
        >
          📷
        </button>

        <input
          type="text"
          className="pwa-chat-input"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Message support..."
        />

        <button
          type="submit"
          disabled={!chatInput.trim() && !pendingImageUrl}
          className="pwa-icon-btn"
          style={{ opacity: (!chatInput.trim() && !pendingImageUrl) ? 0.35 : 1 }}
        >
          ➤
        </button>
      </form>

      {/* Fullscreen Image Modal */}
      {viewImage && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <button
            onClick={() => setViewImage(null)}
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', color: '#fff', fontSize: '32px', cursor: 'pointer' }}
          >
            ×
          </button>
          <img src={viewImage} alt="Expanded" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '12px' }} />
        </div>
      )}
    </div>
  );
};
